import { motion } from "framer-motion";
import {
  monitorInYellowCircle,
  personInYellowCircle,
  unleashCreativityIcon,
} from "../../../assets";

const benefits = [
  {
    number: "01",
    icon: monitorInYellowCircle,
    eyebrow: "LEARN BY DOING",
    title: "Interactive Learning",
    description:
      "Forget passive classroom learning. ZTeens turns technology into something students can touch, build, experiment with and understand.",
    highlight: "Projects over theory.",
  },
  {
    number: "02",
    icon: personInYellowCircle,
    eyebrow: "LEARN FROM PEOPLE",
    title: "Real Tech Mentors",
    description:
      "Students learn directly from people who build technology in the real world — gaining guidance, perspective and the confidence to think bigger.",
    highlight: "Mentorship that matters.",
  },
  {
    number: "03",
    icon: unleashCreativityIcon,
    eyebrow: "THINK DIFFERENT",
    title: "Unleash Creativity",
    description:
      "We give young minds the freedom to experiment, make mistakes, solve problems and turn their own ideas into technology.",
    highlight: "Ideas become inventions.",
  },
];

const WhyZTeens = () => {
  return (
    <section className="relative overflow-hidden py-10 sm:py-24 lg:py-32 bg-white">

      {/* =========================================================
          BACKGROUND
      ========================================================= */}
      <div className="absolute inset-0 pointer-events-none">

        {/* Grid */}
        <div className="absolute inset-0 opacity-[0.025] bg-[linear-gradient(#0f172a_1px,transparent_1px),linear-gradient(90deg,#0f172a_1px,transparent_1px)] bg-[size:40px_40px]" />

        {/* Cyan glow */}
        <motion.div
          className="absolute -left-32 top-20 w-[400px] h-[400px] rounded-full bg-[#59b5cd]/15 blur-[120px]"
          animate={{
            x: [0, 40, 0],
            y: [0, -20, 0],
          }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Yellow glow */}
        <motion.div
          className="absolute -right-32 bottom-10 w-[420px] h-[420px] rounded-full bg-[#ecb32c]/15 blur-[130px]"
          animate={{
            x: [0, -30, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* =========================================================
            HEADER
        ========================================================= */}
        <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-8 lg:gap-20 items-end mb-16">

          {/* Small label */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-[#59b5cd]/20 bg-[#59b5cd]/5 px-4 py-2">
              <span className="w-2 h-2 rounded-full bg-[#ecb32c] animate-pulse" />

              <span className="text-xs font-black tracking-[0.2em] text-[#0f172a]">
                WHY tech4Teens
              </span>
            </div>

            <p className="mt-5 text-sm sm:text-base text-gray-500 max-w-sm">
              More than a coding class. An environment designed to help young
              people discover what they are capable of.
            </p>
          </motion.div>

          {/* Big statement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="ruda text-4xl sm:text-5xl lg:text-[4.5rem] font-black leading-[0.95] tracking-[-0.04em] text-[#0f172a]">
              We don't just
              <br />

              <span className="bg-gradient-to-r from-[#59b5cd] to-[#ecb32c] bg-clip-text text-transparent">
                teach tech.
              </span>

              <br />

              We build{" "}
              <span className="relative inline-block">

                <span className="relative z-10">
                  confidence.
                </span>

                <motion.span
                  className="absolute left-0 right-0 bottom-1 h-3 bg-[#ecb32c]/20 rounded-full -z-0"
                  initial={{ width: 0 }}
                  whileInView={{ width: "100%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.5 }}
                />

              </span>
            </h2>
          </motion.div>

        </div>

        {/* =========================================================
            BENEFIT CARDS
        ========================================================= */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">

          {benefits.map((item, index) => (

            <motion.article
              key={item.number}
              initial={{
                opacity: 0,
                y: 50,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
                margin: "-80px",
              }}
              transition={{
                duration: 0.7,
                delay: index * 0.12,
              }}
              whileHover={{
                y: -10,
              }}
              className="group relative overflow-hidden rounded-[2rem] border border-gray-100 bg-white p-7 sm:p-8 shadow-[0_10px_40px_rgba(15,23,42,0.05)] hover:shadow-[0_25px_70px_rgba(15,23,42,0.12)] transition-shadow duration-500"
            >

              {/* Hover glow */}
              <div className="absolute -right-20 -top-20 w-40 h-40 rounded-full bg-[#59b5cd]/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Number */}
              <div className="flex items-center justify-between">

                <span className="text-xs font-black tracking-[0.25em] text-gray-300">
                  {item.number}
                </span>

                <span className="text-[10px] font-bold tracking-wider text-gray-400">
                  tech4Teens EXPERIENCE
                </span>

              </div>

              {/* Icon */}
              <motion.div
                className="mt-8 w-[72px] h-[72px] rounded-[1.4rem] bg-gray-50 flex items-center justify-center border border-gray-100"
                whileHover={{
                  rotate: [0, -5, 5, 0],
                  scale: 1.08,
                }}
                transition={{
                  duration: 0.5,
                }}
              >
                <img
                  src={item.icon}
                  alt={item.title}
                  className="w-12 h-12 object-contain"
                />
              </motion.div>

              {/* Eyebrow */}
              <p className="mt-7 text-[10px] font-black tracking-[0.2em] text-[#59b5cd]">
                {item.eyebrow}
              </p>

              {/* Title */}
              <h3 className="mt-2 text-2xl sm:text-3xl font-black text-[#0f172a] tracking-tight">
                {item.title}
              </h3>

              {/* Description */}
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-gray-500">
                {item.description}
              </p>

              {/* Bottom highlight */}
              <div className="mt-7 pt-5 border-t border-gray-100 flex items-center justify-between">

                <span className="text-sm font-bold text-[#0f172a]">
                  {item.highlight}
                </span>

                <motion.span
                  className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-[#ecb32c] group-hover:text-[#0f172a] transition-colors duration-300"
                  whileHover={{ x: 4 }}
                >
                  →
                </motion.span>

              </div>

            </motion.article>

          ))}

        </div>

        {/* =========================================================
            PARENT PROMISE / STATEMENT
        ========================================================= */}
        <motion.div
          initial={{
            opacity: 0,
            y: 40,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.7,
          }}
          className="mt-8 rounded-[2rem] bg-[#0f172a] overflow-hidden relative"
        >

          {/* Decorative glow */}
          <div className="absolute -left-20 -top-20 w-64 h-64 rounded-full bg-[#59b5cd]/20 blur-[90px]" />

          <div className="absolute -right-20 -bottom-20 w-64 h-64 rounded-full bg-[#ecb32c]/20 blur-[90px]" />

          <div className="relative z-10 grid md:grid-cols-[1fr_auto] gap-8 items-center p-7 sm:p-10 lg:p-12">

            <div>

              <div className="flex items-center gap-2 mb-4">

                <span className="w-2 h-2 rounded-full bg-[#ecb32c]" />

                <span className="text-xs font-black uppercase tracking-[0.2em] text-[#ecb32c]">
                  Our promise
                </span>

              </div>

              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white ruda max-w-3xl">
                Every student should leave tech4Teens saying:
                <span className="text-[#ecb32c]">
                  {" "}“I can build this.”
                </span>
              </h3>

              <p className="mt-4 text-sm sm:text-base text-gray-400 max-w-2xl leading-relaxed">
                Because the biggest transformation isn't learning another
                programming language. It's discovering that you can take an
                idea in your head and turn it into something real.
              </p>

            </div>

            {/* Visual */}
            <motion.div
              animate={{
                rotate: [0, 5, -5, 0],
                y: [0, -6, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="hidden md:flex w-24 h-24 lg:w-28 lg:h-28 rounded-[2rem] bg-white/10 border border-white/10 items-center justify-center"
            >
              <div className="text-5xl">
                ✦
              </div>
            </motion.div>

          </div>

        </motion.div>

        {/* =========================================================
            MICRO STATS
        ========================================================= */}
        <div className="mt-10 flex flex-wrap justify-center md:justify-between gap-6 text-center md:text-left">

          {[
            ["01", "Hands-on projects"],
            ["02", "Industry mentorship"],
            ["03", "Creative problem solving"],
            ["04", "Future-ready skills"],
          ].map(([number, text], index) => (

            <motion.div
              key={number}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{
                delay: index * 0.1,
              }}
              className="flex items-center gap-3"
            >

              <span className="text-xs font-black text-[#ecb32c]">
                {number}
              </span>

              <span className="text-sm font-semibold text-gray-500">
                {text}
              </span>

            </motion.div>

          ))}

        </div>

      </div>
    </section>
  );
};

export default WhyZTeens;